import { ApiError, networkError, normalizeProblemDetails } from '@/lib/api/errors';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined;

export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>;

/** Resolves the bearer token for the current session, or `null` when signed out. */
export type AccessTokenProvider = () => string | null | Promise<string | null>;

export interface ApiClientConfig {
  readonly baseUrl: string;
  readonly appVersion: string;
  readonly getAccessToken: AccessTokenProvider;
  readonly fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  readonly method?: HttpMethod;
  /** Serialised as JSON unless it is already a `FormData`/`Blob`/string. */
  readonly body?: unknown;
  readonly query?: QueryParams;
  readonly headers?: Readonly<Record<string, string>>;
  readonly signal?: AbortSignal;
  /** Skips the Authorization header — used by the few public endpoints (health, config). */
  readonly anonymous?: boolean;
}

export const REQUEST_ID_HEADER = 'X-Request-Id';

/** `crypto.randomUUID` is unavailable on insecure origins; the fallback only needs uniqueness. */
export function createRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildUrl(baseUrl: string, path: string, query: QueryParams | undefined): string {
  const url = `${baseUrl.replace(/\/+$/u, '')}/${path.replace(/^\/+/u, '')}`;
  if (!query) {
    return url;
  }

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const values = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item !== null && item !== undefined) {
        search.append(key, String(item));
      }
    }
  }

  const serialized = search.toString();
  return serialized === '' ? url : `${url}?${serialized}`;
}

function isRawBody(body: unknown): body is BodyInit {
  return (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer
  );
}

async function readBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();

  if (text === '') {
    return null;
  }
  if (!contentType.includes('json')) {
    return text;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export interface ApiClient {
  request: <TResponse>(path: string, options?: RequestOptions) => Promise<TResponse>;
  get: <TResponse>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<TResponse>;
  post: <TResponse>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<TResponse>;
  put: <TResponse>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<TResponse>;
  patch: <TResponse>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<TResponse>;
  delete: <TResponse>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<TResponse>;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const doFetch = config.fetchImpl ?? globalThis.fetch.bind(globalThis);

  async function request<TResponse>(
    path: string,
    options: RequestOptions = {},
  ): Promise<TResponse> {
    const requestId = createRequestId();
    const headers = new Headers({
      Accept: 'application/json, application/problem+json',
      [REQUEST_ID_HEADER]: requestId,
      'X-Client-Version': config.appVersion,
    });

    for (const [key, value] of Object.entries(options.headers ?? {})) {
      headers.set(key, value);
    }

    if (options.anonymous !== true) {
      const token = await config.getAccessToken();
      if (token !== null && token !== '') {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const init: RequestInit = {
      method: options.method ?? 'GET',
      headers,
      credentials: 'omit',
    };

    if (options.signal) {
      init.signal = options.signal;
    }

    if (options.body !== undefined) {
      if (isRawBody(options.body)) {
        init.body = options.body;
      } else {
        headers.set('Content-Type', 'application/json');
        init.body = JSON.stringify(options.body);
      }
    }

    let response: Response;
    try {
      response = await doFetch(buildUrl(config.baseUrl, path, options.query), init);
    } catch (cause) {
      throw networkError(cause, requestId);
    }

    // The backend echoes the header back; prefer its value so the id survives a proxy rewrite.
    const correlationId = response.headers.get(REQUEST_ID_HEADER) ?? requestId;

    if (response.status === 204 || response.status === 205) {
      return undefined as TResponse;
    }

    const body = await readBody(response);

    if (!response.ok) {
      throw normalizeProblemDetails(response, body, correlationId);
    }

    return body as TResponse;
  }

  return {
    request,
    get: (path, options) => request(path, { ...options, method: 'GET' }),
    post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
    put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
    patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
    delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
  };
}

/**
 * Token provider indirection: the Supabase session lives in a React context, but the API client
 * is a module singleton used by query functions outside the tree. `AuthProvider` pushes the
 * current accessor in here on mount.
 */
let accessTokenProvider: AccessTokenProvider = () => null;

export function setAccessTokenProvider(provider: AccessTokenProvider): void {
  accessTokenProvider = provider;
}

export const api: ApiClient = createApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  appVersion: import.meta.env.VITE_APP_VERSION,
  getAccessToken: () => accessTokenProvider(),
});

export { ApiError };
